import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
  useTab,
} from '../../../components/common/tabs/tab-container';
import Title from '../../../components/common/title';
import { CropWorkType } from '../../../enums/crop-work-types';
import { CROP_YIELD_ACCOUNTING_INDEX } from '../../../router/routes';
import Section from './section';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import css from './index.module.css';
import { useService } from '../../../services/services';

export enum TabKey {
  GRAINS = 'grains',
  VEGETABLE = 'vegetable',
  FRUIT = 'fruit',
  FORAGE = 'forage',
}

export let TabWorkMap = {
  [TabKey.GRAINS]: CropWorkType.THRESHING,
  [TabKey.VEGETABLE]: CropWorkType.VEGETABLE_ETC_HARVESTING,
  [TabKey.FRUIT]: CropWorkType.FRUIT_HARVESTING,
  [TabKey.FORAGE]: CropWorkType.FORAGE_HARVESTING,
};

export default function Index() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.yield_accounting.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const { workService } = useService();

  const [tab, setTab] = useTab(CROP_YIELD_ACCOUNTING_INDEX, [
    'tab',
    'farmId',
    'season',
  ]);

  return (
    <>
      <Title title={tp('title')} />
      <TabContainer
        selectedTabKey={tab}
        setTab={setTab}
        tabNavigation={
          <TabNavigation>
            <TabLink tabKey={TabKey.GRAINS}>{tp('section.grains')}</TabLink>
            <TabLink tabKey={TabKey.VEGETABLE}>
              {tp('section.vegetable')}
            </TabLink>
            <TabLink tabKey={TabKey.FRUIT}>{tp('section.fruit')}</TabLink>
            <TabLink tabKey={TabKey.FORAGE}>{tp('section.forage')}</TabLink>
          </TabNavigation>
        }
        tabContents={
          <TabContents>
            <Tab tabKey={TabKey.GRAINS}>
              <div className={css['excel-btn']}>
                <ExcelButton
                  id={'yield-accounting-' + TabKey.GRAINS + '-excel'}
                  onClick={() => {
                    return workService
                      .exportYieldXls(farmId, season, CropWorkType.THRESHING)
                      .subscribe();
                  }}
                />
              </div>
              <Section workTypeId={TabWorkMap[TabKey.GRAINS]} />
            </Tab>
            <Tab tabKey={TabKey.VEGETABLE}>
              <div className={css['excel-btn']}>
                <ExcelButton
                  id={'yield-accounting-' + TabKey.VEGETABLE + '-excel'}
                  onClick={() => {
                    return workService
                      .exportYieldXls(
                        farmId,
                        season,
                        CropWorkType.VEGETABLE_ETC_HARVESTING
                      )
                      .subscribe();
                  }}
                />
              </div>
              <Section workTypeId={TabWorkMap[TabKey.VEGETABLE]} />
            </Tab>
            <Tab tabKey={TabKey.FRUIT}>
              <div className={css['excel-btn']}>
                <ExcelButton
                  id={'yield-accounting-' + TabKey.FRUIT + '-excel'}
                  onClick={() => {
                    return workService
                      .exportYieldXls(
                        farmId,
                        season,
                        CropWorkType.FRUIT_HARVESTING
                      )
                      .subscribe();
                  }}
                />
              </div>
              <Section workTypeId={TabWorkMap[TabKey.FRUIT]} />
            </Tab>
            <Tab tabKey={TabKey.FORAGE}>
              <div className={css['excel-btn']}>
                <ExcelButton
                  id={'yield-accounting-' + TabKey.FORAGE + '-excel'}
                  onClick={() => {
                    return workService
                      .exportYieldXls(
                        farmId,
                        season,
                        CropWorkType.FORAGE_HARVESTING
                      )
                      .subscribe();
                  }}
                />
              </div>
              <Section workTypeId={TabWorkMap[TabKey.FORAGE]} />
            </Tab>
          </TabContents>
        }
      />
    </>
  );
}
